import { buildDraftedMessageAgentPayload } from '@fruitful/llm-service-shared';
import { type DraftedMessageButtonWidgetProps } from '@fruitful/types';
import React, { useCallback, useState } from 'react';
import { Platform, ScrollView as RNScrollView } from 'react-native';
import {
    ArrowUpIcon,
    BodyMediumRegular,
    H6Regular,
    Icon,
    IconButton,
    Input,
    PenLineIcon,
    ScrollView,
    View,
    XStack,
    YStack,
} from 'smoothie';

import { useNumberKeySelection } from '../../hooks/useNumberKeySelection';
import { CHAT_INPUT_WIDGET } from '../shared-chat/ChatLayout';
import { BaseInputWidget } from './BaseInputWidget';

const FOOTER_OPTIONS_MAX_HEIGHT_KEYBOARD = 160;
const COMPOSER_LEADING_SLOT_SIZE = 40;

const OPTION_ROW_PADDING_HORIZONTAL = 12;
const OPTION_ROW_PADDING_VERTICAL = 4;
const OPTION_ROW_GAP = 12;
const OPTION_ROW_MIN_HEIGHT = 40;

const LOOKS_GOOD_OPTION_ID = 'looks-good';

const CONFIRMATION_OPTIONS = [
    { id: LOOKS_GOOD_OPTION_ID, label: 'Looks good' },
] as const;

const KEYBOARD_OPEN_THRESHOLD = 0.75;

const useWebKeyboardVisible = (): boolean => {
    const [visible, setVisible] = useState(false);

    React.useEffect(() => {
        if (Platform.OS !== 'web' || typeof window === 'undefined') {
            return undefined;
        }

        const visualViewport = window.visualViewport;
        if (visualViewport == null) {
            return undefined;
        }

        const initialHeight = window.innerHeight;

        const handleResize = () => {
            setVisible(
                visualViewport.height < initialHeight * KEYBOARD_OPEN_THRESHOLD,
            );
        };

        visualViewport.addEventListener('resize', handleResize);
        return () => {
            visualViewport.removeEventListener('resize', handleResize);
        };
    }, []);

    return visible;
};

interface ConfirmationMenuWidgetProps extends DraftedMessageButtonWidgetProps {
    disabled?: boolean;
    isKeyboardVisible?: boolean;
    onConfirm: (
        displayText: string,
        agentPayload: string,
    ) => void | Promise<void>;
    onChangeRequest: (message: string) => void;
}

export const ConfirmationMenuWidget = ({
    label,
    confirmationPrefix,
    confirmationPayload,
    disabled = false,
    isKeyboardVisible: isKeyboardVisibleProp = false,
    onConfirm,
    onChangeRequest,
}: ConfirmationMenuWidgetProps) => {
    const webKeyboardVisible = useWebKeyboardVisible();
    const isKeyboardVisible = isKeyboardVisibleProp || webKeyboardVisible;
    const [customInputValue, setCustomInputValue] = useState('');
    const [hasSubmitted, setHasSubmitted] = useState(false);

    const isWidgetDisabled = disabled || hasSubmitted;

    const handleSelectOption = useCallback(
        (optionId: string) => {
            if (isWidgetDisabled) {
                return;
            }

            if (optionId === LOOKS_GOOD_OPTION_ID) {
                const agentPayload = buildDraftedMessageAgentPayload({
                    label,
                    confirmationPrefix,
                    confirmationPayload,
                });
                setHasSubmitted(true);
                void onConfirm(label, agentPayload);
            }
        },
        [
            isWidgetDisabled,
            label,
            confirmationPrefix,
            confirmationPayload,
            onConfirm,
        ],
    );

    const handleSend = useCallback(() => {
        if (isWidgetDisabled) {
            return;
        }

        const customTrimmed = customInputValue.trim();

        if (customTrimmed !== '') {
            setHasSubmitted(true);
            onChangeRequest(customTrimmed);
        }
    }, [isWidgetDisabled, customInputValue, onChangeRequest]);

    // Skipped while focus is in the "Change something..." field so typing a
    // number there doesn't trigger a selection (handled inside the hook).
    useNumberKeySelection({
        enabled: !isWidgetDisabled,
        optionCount: CONFIRMATION_OPTIONS.length,
        onSelectIndex: (index) => {
            const option = CONFIRMATION_OPTIONS[index];
            if (option != null) {
                handleSelectOption(option.id);
            }
        },
    });

    const canSend =
        !hasSubmitted && !isWidgetDisabled && customInputValue.trim() !== '';

    const optionsMaxHeight = isKeyboardVisible
        ? FOOTER_OPTIONS_MAX_HEIGHT_KEYBOARD
        : undefined;

    const optionRows = CONFIRMATION_OPTIONS.map((option, i) => {
        return (
            <XStack
                key={option.id}
                alignItems="center"
                gap={OPTION_ROW_GAP}
                paddingHorizontal={OPTION_ROW_PADDING_HORIZONTAL}
                paddingVertical={OPTION_ROW_PADDING_VERTICAL}
                disabled={isWidgetDisabled}
                cursor={isWidgetDisabled ? 'default' : 'pointer'}
                pointerEvents={isWidgetDisabled ? 'none' : 'auto'}
                onPress={() => {
                    handleSelectOption(option.id);
                }}
                pressStyle={
                    !isWidgetDisabled && {
                        opacity: 0.7,
                    }
                }
            >
                <View
                    width={40}
                    height={40}
                    justifyContent="center"
                    alignItems="center"
                    backgroundColor="$gray200"
                    borderRadius={16}
                >
                    <BodyMediumRegular>{i + 1}</BodyMediumRegular>
                </View>
                <YStack
                    flex={1}
                    minHeight={OPTION_ROW_MIN_HEIGHT}
                    justifyContent="center"
                    pointerEvents="none"
                >
                    <BodyMediumRegular>{option.label}</BodyMediumRegular>
                </YStack>
            </XStack>
        );
    });

    const optionsContent =
        optionsMaxHeight != null ? (
            Platform.OS === 'web' ? (
                <ScrollView maxHeight={optionsMaxHeight}>
                    {optionRows}
                </ScrollView>
            ) : (
                <RNScrollView
                    style={{ maxHeight: optionsMaxHeight }}
                    nestedScrollEnabled
                    keyboardShouldPersistTaps="handled"
                    showsVerticalScrollIndicator={false}
                >
                    {optionRows}
                </RNScrollView>
            )
        ) : (
            optionRows
        );

    return (
        <BaseInputWidget
            alignSelf="stretch"
            width="100%"
            paddingTop={CHAT_INPUT_WIDGET.paddingTop}
        >
            <H6Regular
                paddingHorizontal={CHAT_INPUT_WIDGET.rowPaddingHorizontal}
                paddingBottom={12}
            >
                How does this look?
            </H6Regular>

            {optionsContent}

            <XStack
                alignItems="center"
                gap={OPTION_ROW_GAP}
                minHeight={48}
                paddingHorizontal={OPTION_ROW_PADDING_HORIZONTAL}
                paddingTop={OPTION_ROW_PADDING_VERTICAL}
                paddingBottom={8}
            >
                <XStack
                    width={COMPOSER_LEADING_SLOT_SIZE}
                    height={COMPOSER_LEADING_SLOT_SIZE}
                    alignItems="center"
                    justifyContent="center"
                    flexShrink={0}
                >
                    <Icon icon={PenLineIcon} size={16} color="$gray1000" />
                </XStack>
                <XStack
                    flex={1}
                    minHeight={OPTION_ROW_MIN_HEIGHT}
                    alignItems="center"
                >
                    <Input
                        flex={1}
                        width="100%"
                        size="regular"
                        fontWeight="regular"
                        placeholder="Change something..."
                        placeholderTextColor="$gray1000"
                        value={customInputValue}
                        disabled={isWidgetDisabled}
                        height={40}
                        padding={0}
                        paddingLeft={8}
                        paddingRight={8}
                        paddingVertical={0}
                        borderWidth={0}
                        backgroundColor="transparent"
                        focusStyle={{
                            borderWidth: 0,
                            outlineWidth: 0,
                        }}
                        hoverStyle={{
                            borderWidth: 0,
                            outlineWidth: 0,
                        }}
                        containerProps={{
                            flex: 1,
                            width: '100%',
                            height: 40,
                            alignItems: 'center',
                            backgroundColor: 'transparent',
                        }}
                        onChangeText={setCustomInputValue}
                        onSubmitEditing={handleSend}
                    />
                </XStack>
                <IconButton
                    variant="primary"
                    icon={ArrowUpIcon}
                    disabled={!canSend}
                    onPress={handleSend}
                />
            </XStack>
        </BaseInputWidget>
    );
};
